// Lightweight weighted relevance matcher for blog ↔ product cross-linking.
// Normalises hair-domain synonyms, weights category hits higher than name hits,
// and exposes a minimum-score floor so weak matches don't render as relevant.

const STOP = new Set([
  "the", "and", "for", "with", "your", "you", "our", "are", "was", "but",
  "from", "this", "that", "what", "how", "why", "all", "any", "can", "into",
  "out", "get", "use", "her", "his", "she", "him", "they", "them", "have",
  "has", "had", "will", "just", "not", "one", "two", "new", "now", "more",
  "most", "best", "top", "guide", "guides", "tips", "post", "posts", "blog",
  "shop", "plymhair", "about", "when", "who", "which", "their", "than",
  "then", "also", "very", "much", "some", "such", "make", "made", "should",
  "would", "could", "every", "between", "over", "under", "after", "before",
  "while",
  // Hair-domain noise — present on nearly every product/post, kills signal.
  "hair", "hairs", "style", "styles", "styling", "colour", "color", "colours",
  "colors", "uk", "plymouth", "premium", "luxury", "inch", "inches", "piece",
  "pieces", "pack", "packs", "set", "sets", "size", "sizes", "natural",
  "perfect", "ultimate", "complete", "find", "finding", "right", "flawless",
  "long-lasting", "wear",
]);

// Map of raw token → canonical token. Both sides of a comparison are
// normalised through this map so braiding/braided/braids all collapse to braid.
const SYNONYMS: Record<string, string> = {
  // Braids
  braid: "braid", braids: "braid", braiding: "braid", braided: "braid", braider: "braid",
  // Wigs
  wig: "wig", wigs: "wig", wiggy: "wig",
  // Extensions / weaves
  extension: "extension", extensions: "extension",
  weft: "extension", wefts: "extension", weave: "extension", weaves: "extension",
  // Clip-in
  clipin: "clipin", clipins: "clipin",
  // Ponytail
  pony: "ponytail", ponies: "ponytail", ponytail: "ponytail", ponytails: "ponytail",
  // Locs
  loc: "loc", locs: "loc", locks: "loc", dread: "loc", dreads: "loc",
  dreadlock: "loc", dreadlocks: "loc", faux: "loc",
  // Curly
  curl: "curly", curls: "curly", curly: "curly", wavy: "curly", waves: "curly",
  // Afro / kinky
  kink: "afro", kinks: "afro", kinky: "afro", afro: "afro", afros: "afro",
  // Closures
  closure: "closure", closures: "closure", frontal: "closure", frontals: "closure",
  // Lace front
  lacefront: "lacefront", frontlace: "lacefront",
  // Men's
  man: "mens", men: "mens", male: "mens", mens: "mens",
  toupee: "mens", toupees: "mens",
  // Kids
  kid: "kids", kids: "kids", child: "kids", children: "kids",
  // Care
  wash: "care", washing: "care", washed: "care",
  care: "care", caring: "care", maintain: "care", maintenance: "care",
  protect: "protective", protecting: "care", protective: "protective",
};

function canon(tok: string): string {
  return SYNONYMS[tok] ?? tok;
}

/** Tokenise free text. Pre-collapses common compound forms before splitting. */
function tokenize(...sources: Array<string | null | undefined>): Set<string> {
  const set = new Set<string>();
  for (const raw of sources) {
    if (!raw) continue;
    const pre = raw
      .toLowerCase()
      // Collapse "clip in" / "clip-in" → clipin
      .replace(/clip[\s-]?ins?\b/g, "clipin")
      // Collapse "lace front" / "lace-front" → lacefront
      .replace(/lace[\s-]?front\b/g, "lacefront")
      // Collapse "faux locs"
      .replace(/faux[\s-]?locs?\b/g, "loc");

    pre.split(/[^a-z0-9]+/i).forEach((tok) => {
      if (tok.length < 3) return;
      if (STOP.has(tok)) return;
      set.add(canon(tok));
    });
  }
  return set;
}

/** Minimum total score for a candidate to count as a "real" relevance match. */
export const MIN_RELEVANCE_SCORE = 3;

const STRONG_QUERY_WEIGHT = 2;
const WEAK_QUERY_WEIGHT = 1;
const STRONG_CANDIDATE_WEIGHT = 3;
const WEAK_CANDIDATE_WEIGHT = 1;

interface ScoreSide {
  /** High-signal text (post title, product category). */
  strong?: Array<string | null | undefined>;
  /** Low-signal text (post excerpt, product name). */
  weak?: Array<string | null | undefined>;
}

/**
 * Weighted token-overlap score. Each unique normalised token in `query`
 * contributes (queryWeight × candidateWeight) when present in `candidate`.
 * A category hit on both sides scores 6; a name-only hit scores 1.
 */
export function relevanceScore(query: ScoreSide, candidate: ScoreSide): number {
  const qStrong = tokenize(...(query.strong ?? []));
  const qWeak = tokenize(...(query.weak ?? []));
  // Avoid double-counting tokens present in both query bands.
  for (const t of qStrong) qWeak.delete(t);

  if (qStrong.size === 0 && qWeak.size === 0) return 0;

  const cStrong = tokenize(...(candidate.strong ?? []));
  const cWeak = tokenize(...(candidate.weak ?? []));

  let score = 0;
  const hit = (tok: string, qw: number) => {
    if (cStrong.has(tok)) score += qw * STRONG_CANDIDATE_WEIGHT;
    else if (cWeak.has(tok)) score += qw * WEAK_CANDIDATE_WEIGHT;
  };
  for (const t of qStrong) hit(t, STRONG_QUERY_WEIGHT);
  for (const t of qWeak) hit(t, WEAK_QUERY_WEIGHT);
  return score;
}
